import React, { useEffect } from "react";
import {
  SchedulingLink,
  TimingSettings,
  MeetingSettings,
  AvailabilityType,
} from "@clockwise/schema";
import { Button } from "@clockwise/design-system";
import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { SchedulingLinkCard } from "@ui/scheduling-link-card";
import { AddCircle, Link, Settings, InfoOutlined } from "@material-ui/icons";
import { ExpandableCard } from "@ui/expandable-card";
import { TrackingEvents, useTracking } from "@util/analytics.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { SetRequired } from "@clockwise/client-commons/src/types";
import { greys } from "@styles/color.styles";

const useStyles = makeStyles({
  popper: {
    maxWidth: `168px`,
  },
  infoIcon: {
    height: `15px`,
    width: `15px`,
    color: greys.cloudyBlue,
    marginLeft: `-4px`,
  },
});

export type SidebarLinkMeetingSettings = {
  timingSettings: SetRequired<Partial<TimingSettings>, "durationMinutes" | "timeRange">;
} & Pick<MeetingSettings, "id" | "slug" | "name" | "availabilityType">;

export type SidebarLink = Pick<SchedulingLink, "linkName" | "linkUrl"> & {
  meetingSettings: SidebarLinkMeetingSettings;
};

export interface SchedulingLinksSidebarProps {
  links: SidebarLink[] | null;
  copyTimesLinkEnabled: boolean;
  openShareLinkModal: boolean;
  onCopy: (linkUrl: string, meetingSettingsId: string | null) => Promise<boolean>;
  onOpen: (linkUrl: string, meetingSettingsId: string | null) => Promise<void>;
  onOpenShareModal: (link: SidebarLink | null) => void;
}

const DEFAULT_LINK: SidebarLink = {
  linkName: "",
  linkUrl: "",
  meetingSettings: {
    id: "",
    timingSettings: {
      durationMinutes: 30,
      timeRange: {
        __typename: "LinkTimeRange",
        windowSizeDays: 14,
      },
    },
    name: "Quick Meeting",
    slug: "quick-meeting",
    availabilityType: AvailabilityType.BalanceAvailabilityAndFocus,
  },
};

export const SchedulingLinksSidebar = ({
  links,
  onCopy: onCopyLink,
  openShareLinkModal,
  onOpen,
  onOpenShareModal,
  copyTimesLinkEnabled,
}: SchedulingLinksSidebarProps) => {
  const classes = useStyles();
  const trackEvent = useTracking();
  const [hasExpanded, setHasExpanded] = React.useState(false);
  const linkCount = links?.length ?? 0;
  const linksOrDefault = links ?? [DEFAULT_LINK];

  const onCreateLink = () => {
    window.open(paths.createSchedulingLink, "_blank");
    trackEvent(TrackingEvents.LINKS.SIDEPANEL.CREATE_LINK);
  };
  const onManageLinks = () => {
    window.open(paths.schedulingLinks, "_blank");
    trackEvent(TrackingEvents.LINKS.SIDEPANEL.MANAGE_LINKS);
  };

  useEffect(() => {
    if (openShareLinkModal) {
      setTimeout(() => {
        onOpenShareModal(linksOrDefault[0]);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openShareLinkModal]);

  const tooltipText =
    "Share your availability instantly - Clockwise will find the best time to meet, based on your Focus Time and scheduling needs.";

  return (
    <div className="cw-relative">
      <ExpandableCard
        defaultExpanded={!!links && (linkCount <= 1 || openShareLinkModal)}
        onExpand={(userTouched) => {
          if (!hasExpanded) {
            trackEvent(TrackingEvents.LINKS.SIDEPANEL.EXPANDED, { linkCount, userTouched });
          }
          setHasExpanded(true);
        }}
        onCollapse={() => {
          if (hasExpanded) {
            setHasExpanded(false);
          }
        }}
        expandedHeight={
          /* 172 is height of a single card, 42 height of the create and manage btns */
          linksOrDefault.length === 0
            ? 99
            : Math.min(Math.min(3, linksOrDefault.length) * 172 + 42, 381) // 381 gives us 2 cards with the title of 3rd shown.
        }
        topContent={
          <div
            className="cw-text-sm cw-font-bold cw-text-green-darker cw-flex cw-flex-row cw-items-center"
            cw-id="links-section"
          >
            <Link className="cw-w-[20px] cw-mr-[14px]" />
            <span className="cw-mr-2">Scheduling Links</span>
            <Tooltip placement="bottom" title={tooltipText} classes={{ popper: classes.popper }}>
              <InfoOutlined className={classes.infoIcon} />
            </Tooltip>
          </div>
        }
        bottomContentClass={undefined}
        bottomContent={
          <div>
            <div className="cw-flex cw-gap-2 -cw-ml-[7px] cw-mb-3">
              <Button
                size="small"
                variant="text"
                onClick={onCreateLink}
                cw-id="create-links-button"
                startIcon={AddCircle}
              >
                Create
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={onManageLinks}
                cw-id="manage-links-button"
                startIcon={Settings}
              >
                Manage
              </Button>
            </div>
            {linksOrDefault.map((link, i) => (
              <SchedulingLinkCard
                key={i}
                link={link}
                onCopyLink={onCopyLink}
                onCopyTimes={() => onOpenShareModal(link)}
                onOpen={onOpen}
                className="cw-mb-3"
                shakeShare={i === 0 && openShareLinkModal}
                copyTimesLinkEnabled={copyTimesLinkEnabled}
              />
            ))}
          </div>
        }
      />
    </div>
  );
};
